import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Pencil, Trash2, User, Eye, Calendar, CreditCard, LayoutGrid, List, MapPin } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Search } from 'lucide-react';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  PREVU: 'bg-blue-100 text-blue-800',
  REALISE: 'bg-green-100 text-green-800',
  ANNULE: 'bg-red-100 text-red-800',
  NO_SHOW: 'bg-gray-100 text-gray-800',
};

export default function Clients() {
  const { getAuthHeaders } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [open, setOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [history, setHistory] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [services, setServices] = useState([]);
  const [aptDialogOpen, setAptDialogOpen] = useState(false);
  const [editingApt, setEditingApt] = useState(null);
  const [aptFormData, setAptFormData] = useState({
    service_id: '',
    date: '',
    statut: 'PREVU',
    prix: '',
  });
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    telephone: '',
    email: '',
    adresse: '',
    code_postal: '',
    ville: '',
    notes: '',
  });
  const [zoneFilter, setZoneFilter] = useState('all');

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients`, getAuthHeaders());
      setClients(response.data);
      setFilteredClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/services`, getAuthHeaders());
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  useEffect(() => {
    fetchClients();
    fetchServices();
  }, []);

  useEffect(() => {
    let result = clients;
    const query = searchQuery.trim().toLowerCase();
    if (query !== '') {
      result = result.filter(client =>
        client.prenom.toLowerCase().includes(query) ||
        client.nom.toLowerCase().includes(query) ||
        (client.email && client.email.toLowerCase().includes(query)) ||
        (client.telephone && client.telephone.includes(query)) ||
        (client.ville && client.ville.toLowerCase().includes(query)) ||
        (client.code_postal && client.code_postal.includes(query))
      );
    }
    if (zoneFilter !== 'all') {
      if (zoneFilter === '__none__') {
        result = result.filter(client => !(client.code_postal || '').trim() && !(client.ville || '').trim());
      } else {
        result = result.filter(client => `${(client.code_postal || '').trim()}|${(client.ville || '').trim().toLowerCase()}` === zoneFilter);
      }
    }
    setFilteredClients(result);
  }, [searchQuery, zoneFilter, clients]);

  const zoneOptions = React.useMemo(() => {
    const map = new Map();
    let hasNone = false;
    clients.forEach(c => {
      const cp = (c.code_postal || '').trim();
      const ville = (c.ville || '').trim();
      if (cp || ville) {
        const key = `${cp}|${ville.toLowerCase()}`;
        const label = cp && ville ? `${cp} ${ville}` : (ville || cp);
        if (!map.has(key)) map.set(key, label);
      } else {
        hasNone = true;
      }
    });
    const opts = [{ value: 'all', label: 'Toutes les zones' }];
    Array.from(map.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .forEach(([value, label]) => opts.push({ value, label }));
    if (hasNone) opts.push({ value: '__none__', label: 'Sans adresse' });
    return opts;
  }, [clients]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Normalise les champs vides en null (EmailStr refuse une chaîne vide côté backend)
    const payload = Object.fromEntries(
      Object.entries(formData).map(([k, v]) => [k, typeof v === 'string' && v.trim() === '' ? null : v])
    );
    try {
      if (editingClient) {
        await axios.put(`${API_URL}/api/clients/${editingClient.id}`, payload, getAuthHeaders());
        toast.success('Client·e modifié·e');
      } else {
        await axios.post(`${API_URL}/api/clients`, payload, getAuthHeaders());
        toast.success('Client·e créé·e');
      }
      fetchClients();
      handleClose();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/clients/${deleteId}`, getAuthHeaders());
      toast.success('Client·e supprimé·e');
      fetchClients();
      setDeleteId(null);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      prenom: client.prenom,
      nom: client.nom,
      telephone: client.telephone || '',
      email: client.email || '',
      adresse: client.adresse || '',
      code_postal: client.code_postal || '',
      ville: client.ville || '',
      notes: client.notes || '',
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingClient(null);
    setFormData({ prenom: '', nom: '', telephone: '', email: '', adresse: '', code_postal: '', ville: '', notes: '' });
  };

  const getZoneLabel = (client) => {
    const cp = (client.code_postal || '').trim();
    const ville = (client.ville || '').trim();
    if (cp && ville) return `${cp} ${ville}`;
    if (ville) return ville;
    if (cp) return cp;
    return '';
  };

  const viewHistory = async (client) => {
    setSelectedClient(client);
    try {
      const response = await axios.get(`${API_URL}/api/clients/${client.id}/history`, getAuthHeaders());
      setHistory(response.data);
      setHistoryOpen(true);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'historique');
    }
  };

  // Ouvre automatiquement la fiche d'un·e client·e si ?client=<id> est présent (venant de la carte)
  useEffect(() => {
    const clientId = searchParams.get('client');
    if (clientId && clients.length > 0) {
      const target = clients.find((c) => c.id === clientId);
      if (target) {
        viewHistory(target);
      }
      searchParams.delete('client');
      setSearchParams(searchParams, { replace: true });
    }
  }, [clients, searchParams]);

  const refreshHistory = async (clientId) => {
    try {
      const response = await axios.get(`${API_URL}/api/clients/${clientId}/history`, getAuthHeaders());
      setHistory(response.data);
    } catch (error) {
      console.error('Error refreshing history:', error);
    }
  };

  const getServiceName = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    return service ? service.nom : 'Service inconnu';
  };

  const openAptEdit = (apt) => {
    setEditingApt(apt);
    setAptFormData({
      service_id: apt.service_id,
      date: apt.date.slice(0, 16),
      statut: apt.statut,
      prix: apt.prix.toString(),
    });
    setAptDialogOpen(true);
  };

  const handleAptServiceChange = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    setAptFormData({
      ...aptFormData,
      service_id: serviceId,
      prix: service ? service.prix.toString() : aptFormData.prix,
    });
  };

  const handleAptSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        client_id: selectedClient.id,
        service_id: aptFormData.service_id,
        date: aptFormData.date,
        statut: aptFormData.statut,
        prix: parseFloat(aptFormData.prix),
      };
      await axios.put(`${API_URL}/api/appointments/${editingApt.id}`, payload, getAuthHeaders());
      toast.success('Rendez-vous modifié');
      setAptDialogOpen(false);
      setEditingApt(null);
      await refreshHistory(selectedClient.id);
    } catch (error) {
      toast.error('Erreur lors de la modification du rendez-vous');
    }
  };

  const getTotalSpent = () => {
    if (!history) return 0;
    return history.payments.reduce((sum, p) => sum + p.montant, 0);
  };

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Client·e·s</h1>
          <p className="text-muted-foreground mt-2">Gérez votre portefeuille client·e·s</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full w-full sm:w-auto" data-testid="btn-add-client">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau·elle client·e
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="client-dialog" className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingClient ? 'Modifier' : 'Nouveau·elle'} client·e</DialogTitle>
              <DialogDescription>
                Renseignez les informations du/de la client·e
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="prenom">Prénom *</Label>
                  <Input
                    id="prenom"
                    value={formData.prenom}
                    onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                    required
                    data-testid="input-prenom"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nom">Nom *</Label>
                  <Input
                    id="nom"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    required
                    data-testid="input-nom"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="telephone">Téléphone</Label>
                <Input
                  id="telephone"
                  value={formData.telephone}
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                  data-testid="input-telephone"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="input-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="adresse">Adresse</Label>
                <Input
                  id="adresse"
                  placeholder="N° et nom de rue"
                  value={formData.adresse}
                  onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                  data-testid="input-adresse"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code_postal">Code postal</Label>
                  <Input
                    id="code_postal"
                    placeholder="75011"
                    value={formData.code_postal}
                    onChange={(e) => setFormData({ ...formData, code_postal: e.target.value })}
                    data-testid="input-code-postal"
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="ville">Ville</Label>
                  <Input
                    id="ville"
                    placeholder="Paris"
                    value={formData.ville}
                    onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                    data-testid="input-ville"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Input
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  data-testid="input-notes"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleClose}>Annuler</Button>
                <Button type="submit" data-testid="btn-submit-client">Enregistrer</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {clients.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Rechercher par nom, prénom, email, téléphone ou ville..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-clients"
            />
          </div>
          <div className="w-full sm:w-64" data-testid="zone-filter">
            <Combobox
              options={zoneOptions}
              value={zoneFilter}
              onValueChange={(value) => setZoneFilter(value || 'all')}
              placeholder="Filtrer par zone"
              searchPlaceholder="Rechercher une zone..."
              emptyText="Aucune zone trouvée"
            />
          </div>
          <ToggleGroup type="single" value={viewMode} onValueChange={(v) => v && setViewMode(v)} data-testid="view-toggle">
            <ToggleGroupItem value="grid" aria-label="Vue grille">
              <LayoutGrid className="w-4 h-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="list" aria-label="Vue liste">
              <List className="w-4 h-4" />
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      )}

      {filteredClients.length === 0 && clients.length > 0 ? (
        <Card className="rounded-xl border-border/60">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Search className="w-16 h-16 text-muted-foreground mb-4" strokeWidth={1} />
            <p className="text-muted-foreground">Aucun·e client·e trouvé·e pour &laquo;&nbsp;{searchQuery}&nbsp;&raquo;</p>
          </CardContent>
        </Card>
      ) : filteredClients.length === 0 ? (
        <Card className="rounded-xl border-border/60">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <User className="w-16 h-16 text-muted-foreground mb-4" strokeWidth={1} />
            <p className="text-muted-foreground">Aucun·e client·e pour le moment</p>
          </CardContent>
        </Card>
      ) : viewMode === 'list' ? (
        <Card className="rounded-xl border-border/60">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Téléphone</TableHead>
                <TableHead>Zone</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredClients.map((client) => (
                <TableRow key={client.id} data-testid={`client-row-${client.id}`}>
                  <TableCell className="font-medium">{client.prenom} {client.nom}</TableCell>
                  <TableCell>{client.telephone || '-'}</TableCell>
                  <TableCell>{getZoneLabel(client) || '-'}</TableCell>
                  <TableCell>{client.email || '-'}</TableCell>
                  <TableCell className="max-w-[200px] truncate">{client.notes || '-'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => viewHistory(client)} data-testid={`btn-history-${client.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(client)} data-testid={`btn-edit-${client.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteId(client.id)} className="text-destructive hover:text-destructive" data-testid={`btn-delete-${client.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {filteredClients.map((client) => (
            <Card
              key={client.id}
              className="rounded-xl border-border/60 hover:border-primary/20 transition-colors"
              data-testid={`client-card-${client.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-6 h-6 text-primary" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">
                        {client.prenom} {client.nom}
                      </h3>
                    </div>
                  </div>
                </div>
                {client.telephone && (
                  <p className="text-sm text-muted-foreground mb-1">
                    Tél: {client.telephone}
                  </p>
                )}
                {client.email && (
                  <p className="text-sm text-muted-foreground mb-1">
                    {client.email}
                  </p>
                )}
                {getZoneLabel(client) && (
                  <p className="text-sm text-muted-foreground mb-1 flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
                    {getZoneLabel(client)}
                  </p>
                )}
                {client.notes && (
                  <p className="text-sm text-muted-foreground mt-3 italic">
                    {client.notes}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border/40">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => viewHistory(client)}
                    data-testid={`btn-history-${client.id}`}
                    className="flex-1 min-w-[100px]"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Historique
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(client)}
                    data-testid={`btn-edit-${client.id}`}
                    className="flex-1 min-w-[100px]"
                  >
                    <Pencil className="w-4 h-4 mr-1" />
                    Modifier
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleteId(client.id)}
                    className="text-destructive hover:text-destructive flex-1 min-w-[100px]"
                    data-testid={`btn-delete-${client.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Supprimer
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle>
              Historique de {selectedClient?.prenom} {selectedClient?.nom}
            </DialogTitle>
            <DialogDescription>
              Récapitulatif des rendez-vous et paiements
            </DialogDescription>
          </DialogHeader>
          {history && (
            <div className="space-y-6">
              <Card className="rounded-xl border-border/60 bg-gradient-to-br from-green-50 to-emerald-50">
                <CardContent className="p-6">
                  <p className="text-sm text-muted-foreground mb-1">Total dépensé</p>
                  <p className="text-3xl font-bold text-green-700">{getTotalSpent().toFixed(2)} €</p>
                </CardContent>
              </Card>

              <div>
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Rendez-vous ({history.appointments.length})
                </h3>
                <div className="space-y-2">
                  {history.appointments.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Aucun rendez-vous</p>
                  ) : (
                    history.appointments.map((apt) => (
                      <div
                        key={apt.id}
                        className="p-4 rounded-lg bg-muted/30 border border-border/40 flex justify-between items-center gap-3"
                        data-testid={`history-apt-${apt.id}`}
                      >
                        <div className="min-w-0">
                          <p className="font-medium">
                            {format(new Date(apt.date), 'PPP à HH:mm', { locale: fr })}
                          </p>
                          <p className="text-sm text-muted-foreground truncate">{getServiceName(apt.service_id)}</p>
                          <Badge className={`mt-1 ${statusColors[apt.statut] || ''}`}>{apt.statut}</Badge>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <p className="font-bold whitespace-nowrap">{apt.prix.toFixed(2)} €</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openAptEdit(apt)}
                            data-testid={`btn-edit-history-apt-${apt.id}`}
                          >
                            <Pencil className="w-4 h-4 mr-1" />
                            Modifier
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Paiements ({history.payments.length})
                </h3>
                <div className="space-y-2">
                  {history.payments.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Aucun paiement</p>
                  ) : (
                    history.payments.map((payment) => (
                      <div
                        key={payment.id}
                        className="p-4 rounded-lg bg-muted/30 border border-border/40 flex justify-between items-center"
                      >
                        <div>
                          <p className="font-medium">
                            {format(new Date(payment.paid_at), 'PPP', { locale: fr })}
                          </p>
                          <div className="flex gap-2 mt-1">
                            <Badge variant="outline">{payment.type}</Badge>
                            <Badge variant="outline">{payment.moyen}</Badge>
                          </div>
                        </div>
                        <p className="font-bold text-green-600">{payment.montant.toFixed(2)} €</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={aptDialogOpen} onOpenChange={setAptDialogOpen}>
        <DialogContent data-testid="history-apt-dialog" className="max-w-lg w-[95vw]">
          <DialogHeader>
            <DialogTitle>Modifier le rendez-vous</DialogTitle>
            <DialogDescription>
              {selectedClient ? `${selectedClient.prenom} ${selectedClient.nom}` : ''}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAptSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="apt-service">Prestation *</Label>
              <Select value={aptFormData.service_id} onValueChange={handleAptServiceChange} required>
                <SelectTrigger data-testid="history-select-service">
                  <SelectValue placeholder="Sélectionnez une prestation" />
                </SelectTrigger>
                <SelectContent>
                  {services.map(service => (
                    <SelectItem key={service.id} value={service.id}>
                      {service.nom} - {service.prix}€
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="apt-date">Date et heure *</Label>
              <Input
                id="apt-date"
                type="datetime-local"
                value={aptFormData.date}
                onChange={(e) => setAptFormData({ ...aptFormData, date: e.target.value })}
                required
                data-testid="history-input-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="apt-statut">Statut *</Label>
              <Select value={aptFormData.statut} onValueChange={(value) => setAptFormData({ ...aptFormData, statut: value })}>
                <SelectTrigger data-testid="history-select-statut">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PREVU">Prévu</SelectItem>
                  <SelectItem value="REALISE">Réalisé</SelectItem>
                  <SelectItem value="ANNULE">Annulé</SelectItem>
                  <SelectItem value="NO_SHOW">No-show</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="apt-prix">Prix (€) *</Label>
              <Input
                id="apt-prix"
                type="number"
                step="0.01"
                value={aptFormData.prix}
                onChange={(e) => setAptFormData({ ...aptFormData, prix: e.target.value })}
                required
                data-testid="history-input-prix"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAptDialogOpen(false)}>Annuler</Button>
              <Button type="submit" data-testid="btn-submit-history-apt">Enregistrer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Êtes-vous sûr ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le/La client·e sera définitivement supprimé·e.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-cancel-delete">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="btn-confirm-delete">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}